/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {HttpClient} from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
} from "@angular/core";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {PatientTreatmentPlanBaseState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-base.state";
import {ModalController} from "@ionic/angular";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {TrecTherapy} from "@trec/api/clinical-data";
import {TherapyTemplateGroup} from "@trec/api/drug-store";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {StoreAction} from "@trec/components/store";
import {Observable, Subscription, combineLatest} from "rxjs";
import {startWith} from "rxjs/operators";
import {TpBaseActions} from "../../../store/patient-treatment-plan-base.actions";
import {CdItemFormComponent, CdItemFormMode} from "../../forms/cd-item-form-component";
import {FormTherapyOncologicalComponent} from "../../forms/form-therapy-oncological/form-therapy-oncological.component";
import {
  ForTherapyInsertDefaults,
  FormTherapyComponent,
} from "../../forms/form-therapy/form-therapy.component";

export interface EditTherapyDialogData {
  therapy: TrecTherapy | null;
  insulinTherapy: boolean;
  oncoTherapy?: boolean;
  context: RegistryContexts[] | null;
  insertDefaults?: ForTherapyInsertDefaults;
  canEditOrDelete?: boolean
}

@Component({
  selector: "c3-edit-therapy-dialog",
  templateUrl: "./edit-therapy-dialog.component.html",
  styleUrls: ["./edit-therapy-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EditTherapyDialogComponent implements AfterViewInit, OnDestroy {
  @Select(hasActionsExecuting([TpBaseActions.SearchTherapyTemplateGroups]))
  ttSearchLoading$: Observable<boolean>;
  @Select(PatientTreatmentPlanBaseState.therapyTemplateGroupsListSearchResults)
  ttSearchResults$: Observable<TherapyTemplateGroup[] | undefined>;

  @Select(
    hasActionsExecuting([
      TpBaseActions.FetchTherapies,
      TpBaseActions.InsertTherapy,
      TpBaseActions.EditTherapy,
    ])
  )
  loading$: Observable<boolean>;

  @Input()
  data: EditTherapyDialogData;

  @ViewChildren(FormTherapyComponent)
  formGenericItem: QueryList<FormTherapyComponent>;

  @ViewChildren(FormTherapyOncologicalComponent)
  formOncoItem: QueryList<FormTherapyOncologicalComponent>;

  editMode = false;
  isAI = false;

  private _mode: CdItemFormMode = "view";
  public get mode(): CdItemFormMode {
    return this._mode;
  }
  public set mode(value: CdItemFormMode) {
    this._mode = value;
    this.handleModeUpdate();
  }

  therapyTemplateGroup: TherapyTemplateGroup | null = null;

  formItems: CdItemFormComponent<TrecTherapy | TrecTherapy[], unknown>[] | null = null;

  get formsValid(): boolean {
    return this.formItems?.reduce((a, b) => a && b.form.valid, true) ?? false;
  }

  get formsTouched(): boolean {
    return this.formItems?.reduce((a, b) => a && b.form.touched, true) ?? false;
  }

  get formsPristine(): boolean {
    return this.formItems?.reduce((a, b) => a || b.form.pristine, false) ?? false;
  }

  readonly CTX_ONCO = RegistryContexts.ONCOLOGY;
  readonly CTX_ONCOLOGY_BREAST_CANCER = RegistryContexts.ONCOLOGY_BREAST_CANCER;

  private readonly subscr = new Subscription();

  constructor(
    @Inject(HttpClient) protected http: HttpClient,
    private store: Store,
    public modalCtrl: ModalController,
    private c3SnackbarStatus: C3SnackbarStatusService
  ) {
    //console.log("DATA Constructor", this.data);

  }

  ngAfterViewInit() {
    if (!this.data.therapy) {
      this.mode = "insert";
    }

    if (this.data.therapy?.data?.AI) {
      this.editMode = true;
      this.isAI = true;
      this.mode = 'insertByAV';
    }

    this.subscr.add(
      combineLatest([
        // Use startWith beacause combineLatest does not emit until all sources have emitted at least one time
        this.formGenericItem.changes.pipe(startWith([])),
        this.formOncoItem.changes.pipe(startWith([])),
      ]).subscribe(() => {
        this.reEvaluateFormItems();
      })
    );
    //console.log("DATA", this.data);
  }

  onSubmit(formComps: CdItemFormComponent<TrecTherapy | TrecTherapy[], unknown>[] | null) {
    if (!formComps || formComps.length === 0) {
      throw new Error("Try to submit therapy form, but it missing!");
    }

    // Prepare item to submit
    const formRes = formComps.map((formComp) => formComp.prepareData());

    if (formRes.includes(null)) {
      return;
    }

    const therapies: TrecTherapy[] = formRes.reduce(
      (a: TrecTherapy[], b: TrecTherapy) => (Array.isArray(b) ? [...b, ...a] : [b, ...a]),
      [] as TrecTherapy[]
    );

    // If selected, add therapy template group to tags
    if (this.therapyTemplateGroup) {
      therapies.map((t) => t.addTags([...`ttgroup:${this.therapyTemplateGroup?.id}`]));
    }

    let action: StoreAction;
    let succMsg: string;

    switch (this.mode) {
      case "insert":
        action = new TpBaseActions.InsertTherapy({therapies});
        succMsg = `Terapia aggiunta correttamente`;
        break;
      case "insertByAV":
        action = new TpBaseActions.InsertTherapy({therapies});
        succMsg = `Terapia aggiunta correttamente mediante assistente vocale`;
        break;
      case "edit":
        action = new TpBaseActions.EditTherapy({therapies});
        succMsg = `Terapia modificata correttamente`;
        break;
      default:
        throw new Error(`Cannot do operation in mode ${this.mode}`);
    }

    // Perform action
    this.store.dispatch(action).subscribe(() => {
      this.c3SnackbarStatus.showSuccess({message: succMsg});
      this.modalCtrl.dismiss();
    });
  }

  onTherapyTemplateGroupSearch(searchValue: string) {
    if (searchValue && typeof searchValue === "string") {
      this.store.dispatch(new TpBaseActions.SearchTherapyTemplateGroups({query: searchValue}));
    }
  }

  onTherapyTemplateGroupSelected(tt: TherapyTemplateGroup | null) {
    this.therapyTemplateGroup = tt;
  }

  onClose() {
    this.modalCtrl.dismiss();
  }

  private handleModeUpdate() {
    if (this.mode === "edit") {
      // Highlight errors if any
      this.formItems?.forEach((formItem) => {
        formItem.form.markAllAsTouched();
        formItem.form.updateValueAndValidity();
      });
    }
  }

  private reEvaluateFormItems() {
    this.formItems = this.data.oncoTherapy
      ? this.formOncoItem.toArray()
      : this.formGenericItem.toArray();
    this.handleModeUpdate();
  }

  ngOnDestroy() {
    this.store.dispatch(new TpBaseActions.ClearTherapyTemplateGroupSearchResultsList());
    this.subscr.unsubscribe();
  }
}
